import { logout } from '@/app/initialize';
import { store } from '@/app/store';
import { performDataSync } from '@/app/sync';
import { syncUserProfiles } from '@/app/sync/profiles';
import { hideFullPageLoader, showFullPageLoader } from '@/components/FullPageLoader/fullPageLoaderService';
import { connect, EIM, register, setUserProfile } from '@/infrastructure/sdk';
import { deferExecution } from '@/infrastructure/sync/utils';
import { AUTH_CONSTANTS } from '@/shared/constants';
import { logger, showErrorToast } from '@/shared/utils';
import storage from '@/shared/utils/storage';

const { RECONNECT_BATCH_LIMIT, RECONNECT_RETRY_GAP, RECONNECT_LONG_GAP } = AUTH_CONSTANTS;

// Helper function to add delay between retries
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

class AuthManager {
   constructor() {
      this.isDisconnected = false;
      this.isReconnecting = false;
      this.hasInitialSyncCompleted = false;
   }

   setDisconnected(status) {
      logger.log(`[AuthManager] connection status change: disconnected=${status}`);
      this.isDisconnected = status;
   }

   async mirrorflyRegister({ userIdentifier, retryAttempt = 1, email = null } = {}) {
      try {
         const username = userIdentifier || storage.getItem('username');
         if (!username) {
            throw new Error('No username provided');
         }
         let domain = email ? email.match(/@[^@]*$/)?.[0] : null;
         const deeplink = store.getState().inviteData?.pendingToken;

         const registerRes = await register(username, true, {}, domain, deeplink);

         if (registerRes?.statusCode === 200) {
            // Store credentials and attempt login
            storage.setItem('username', registerRes?.data?.username);
            storage.setItem('password', registerRes?.data?.password);
            // Attempt login after successful registration
            const loginRes = await this.mirrorflyLogin();
            return {
               ...registerRes,
               ...loginRes,
               isProfileUpdated: registerRes?.data?.isProfileUpdated,
            };
         }

         return registerRes;
      } catch (error) {
         logger.error(`Registration failed [Attempt ${retryAttempt}]:`, error.message || error);
         showErrorToast(error.message || 'Registration failed', { autoClose: false });
         throw error;
      }
   }

   async mirrorflyLogin(retryAttempt = 1) {
      try {
         const authFlow = storage.getItem('auth_flow');
         if (authFlow === 'updateProfile') {
            showFullPageLoader('UPDATING_PROFILE');
         }
         const username = storage.getItem('username');
         const password = storage.getItem('password');

         if (!username || !password) {
            throw new Error('No credentials found in storage');
         }

         const loginRes = await connect(username, password, true);

         if (loginRes?.statusCode === 200) {
            await syncUserProfiles([username], { isSyncCheckNeeded: false });
            setTimeout(() => {
               hideFullPageLoader('UPDATING_PROFILE');
            }, 500);
            if (authFlow === 'dashboard') {
               const showProgress = !this.hasInitialSyncCompleted;
               this.hasInitialSyncCompleted = true;
               deferExecution(() => performDataSync({ showProgress }));
            }
            return loginRes;
         } else if (loginRes.message === 'Invalid Credentials') {
            logout();
            return;
         }
         return loginRes;
      } catch (error) {
         logger.error(`Login failed [Attempt ${retryAttempt}]:`, error.message || error);
         showErrorToast(error.message || 'Login failed', { autoClose: false });

         if (error.message === 'Invalid Credentials') {
            storage.removeItem('username');
            storage.removeItem('password');
            window.location.reload();
            return;
         }
         throw error;
      }
   }

   async mirrorflyUpdateProfile({ name, image = '', status, mobileNumber, email } = {}) {
      try {
         const username = storage.getItem('username');
         if (!username) {
            throw new Error('No username provided');
         }

         const updateRes = await setUserProfile(name, image, status, mobileNumber, email);

         if (updateRes?.statusCode === 200) {
            return { ...updateRes, isProfileUpdated: true };
         }

         return updateRes;
      } catch (error) {
         logger.error(`Update profile failed:`, error.message || error);
         throw error;
      }
   }

   async mirrorflyReconnectWithRetries() {
      if (this.isReconnecting) return;
      this.isReconnecting = true;
      logger.log('[AuthManager] Starting auto-reconnection flow...', this.isDisconnected);

      while (this.isDisconnected) {
         for (let i = 1; i <= RECONNECT_BATCH_LIMIT; i++) {
            if (!this.isDisconnected) break;
            logger.log(`[AuthManager] Reconnection hit ${i} of batch...`);

            try {
               const res = await this.mirrorflyLogin();
               
               if (res && [400, 401, 403].includes(res.statusCode)) {
                  logger.error(`[AuthManager] Critical auth error ${res.statusCode} during reconnect. Terminating loop and logging out.`);
                  this.isDisconnected = false;
                  this.isReconnecting = false;
                  logout();
                  return;
               }

               if (res?.statusCode === 200) {
                  this.isDisconnected = false;
                  this.isReconnecting = false;
                  logger.log('[AuthManager] Successfully reconnected and synced!');
                  return res;
               }
            } catch (err) {
               logger.warn(`[AuthManager] Reconnection hit ${i} failed:`, err.message);
               
               if (err && [400, 401, 403].includes(err.statusCode)) {
                  logger.error(`[AuthManager] Critical auth error ${err.statusCode} during reconnect. Terminating loop and logging out.`);
                  this.isDisconnected = false;
                  this.isReconnecting = false;
                  logout();
                  return;
               }
            }

            if (i < RECONNECT_BATCH_LIMIT && this.isDisconnected) {
               logger.log(`[AuthManager] Waiting ${RECONNECT_RETRY_GAP / 1000}s for next hit in batch...`);
               await delay(RECONNECT_RETRY_GAP);
            }
         }

         if (this.isDisconnected) {
            logger.log(`[AuthManager] Batch failed. Waiting ${RECONNECT_LONG_GAP / 1000}s for next batch...`);
            await delay(RECONNECT_LONG_GAP);
         }
      }
      this.isReconnecting = false;
   }
}

export const authManager = new AuthManager();

// Maintain legacy exports for compatibility
export const mirrorflyLogin = authManager.mirrorflyLogin.bind(authManager);
export const mirrorflyRegister = authManager.mirrorflyRegister.bind(authManager);
export const mirrorflyUpdateProfile = authManager.mirrorflyUpdateProfile.bind(authManager);
export const validateMobileNumber = EIM.validateMobileNumber;
export { deferExecution };
